import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, AlertTriangle, Tag, MapPin, ChevronRight, ChevronLeft, ChevronDown, ShieldCheck, Zap, ListPlus, ZoomIn, Truck, Lock, MessageCircle } from 'lucide-react';
import BrandLogo from './BrandLogo';
import { Product, Branch } from '../types';
import { BRANCHES } from '../config/siteConfig';
import WhatsAppProductButton from './WhatsAppProductButton';
import { useCartStore } from '../hooks/useCartStore';

import { ArrowLeft } from 'lucide-react';

import { productService } from '../services/ProductService';
import ProductCard from './ProductCard';
import ShippingFormModal from './ShippingFormModal';
import { reviewService } from '../services/ReviewService';
import { ProductReview } from '../types';
import { ReviewSummary } from './reviews/ReviewSummary';
import { CustomerMediaCarousel } from './reviews/CustomerMediaCarousel';
import { ReviewFeed } from './reviews/ReviewFeed';

interface ProductDetailViewProps {
    product: Product;
    currentBranch: Branch;
    onBack: () => void;
}

const ProductDetailView: React.FC<ProductDetailViewProps> = ({ product, currentBranch, onBack }) => {
    const [showAllBranches, setShowAllBranches] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isZooming, setIsZooming] = useState(false);
    const [isShippingModalOpen, setIsShippingModalOpen] = useState(false);

    // Variant State
    const [selectedModel, setSelectedModel] = useState<string | null>(null);
    const [selectedColor, setSelectedColor] = useState<string | null>(null);

    // Related Products State
    const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);

    // Reviews State
    const [reviews, setReviews] = useState<ProductReview[]>([]);

    React.useEffect(() => {
        if (product) {
            reviewService.getAllApprovedReviews().then(data => setReviews(data));
        }
    }, [product]);

    // Removed auto-selection effect so variants are empty by default
    // Removed early return to comply with Rules of Hooks
    // if (!product) return null;

    // Start by getting all standard variants images
    const baseImages = product?.images && product.images.length > 0
        ? product.images
        : (product?.image ? [product.image] : []);

    const variants = product?.variants || [];

    // Get the specific model from currently selected variant
    const currentVariantModel = variants.find(v => v.color_name === selectedColor)?.models?.find(m => (m.model_name || 'Standard') === selectedModel);

    // Inject the model's special image if it exists into the gallery, or just use it as the main one
    const modelSpecificImage = currentVariantModel?.image_url;

    // We build the final images array: 
    // 1. Specific Model Image (if any)
    // 2. Main images from the selected variant (from selected Color)
    // 3. The general product baseImages (fallback)

    // Find the currently selected variant object FIRST so we can get its images
    const selectedVariant = variants.find(v =>
        v.color_name === selectedColor &&
        (v.models || []).some(m => (m.model_name || 'Standard') === selectedModel)
    );

    // Combine them, ensuring no exact duplicates
    const combinedSet = new Set<string>();

    const hasSpecificImage = !!modelSpecificImage || !!selectedVariant?.image_url;

    // Prioritize Model Specific Image > Variant Image > Base Images
    if (modelSpecificImage) {
        combinedSet.add(modelSpecificImage);
    } else if (selectedVariant?.image_url) {
        combinedSet.add(selectedVariant.image_url);
    }

    // Only fallback to the general product images if there is absolutely no specific image for this selection,
    // or if the user hasn't selected a valid variant yet.
    if (!hasSpecificImage) {
        baseImages.forEach(img => combinedSet.add(img));
    }

    // Convert to array and strictly filter out any empty strings or nulls that might have snuck in
    const images = Array.from(combinedSet).filter(Boolean);


    // Filtered Variants Logic
    const allModelNames = variants.flatMap(v => (v.models || []).map(m => m.model_name || 'Standard'));
    const uniqueModels = Array.from(new Set(allModelNames)).sort();

    // Get available colors for selected model (or all colors if no model is selected yet)
    const filteredVariants = selectedModel
        ? variants.filter(v => (v.models || []).some(m => (m.model_name || 'Standard') === selectedModel))
        : variants;

    // Deduplicate colors to avoid React key errors
    const uniqueColorVariants = Array.from(
        new Map<string, any>(filteredVariants.map(v => [v.color_name, v])).values()
    );
    const availableColors = uniqueColorVariants.map((v: any) => {
        let in_stock = false;
        if (selectedModel) {
            const modelInfo = (v.models || []).find((m: any) => (m.model_name || 'Standard') === selectedModel);
            in_stock = modelInfo ? modelInfo.in_stock : false;
        } else {
            // If no model selected, treat color as in stock if ANY model of this color has stock
            in_stock = (v.models || []).some((m: any) => m.in_stock);
        }

        return {
            name: v.color_name,
            code: v.color_code,
            hex: v.color_hex,
            texture: v.color_name.toLowerCase().includes('carbon') ? 'carbon' :
                v.color_name.toLowerCase().includes('forged') ? 'forged' :
                    v.color_name.toLowerCase().includes('alcantara') ? 'alcantara' :
                        v.color_name.toLowerCase().includes('leather') ? 'leather' : 'default',
            image: v.image_url,
            texture_url: v.texture_url,
            in_stock: in_stock
        };
    });

    // (selectedVariant logic was moved up to be used by the images array builder)

    // Get stock status for the specific model in the selected variant
    const currentModelStock = currentVariantModel?.in_stock;

    // Update image when variant or model changes
    React.useEffect(() => {
        // If we have a model-specific image, we just set index to 0 since we prepended it
        if (modelSpecificImage) {
            setCurrentImageIndex(0);
        } else if (selectedVariant?.image_url) {
            // Check if this image is in the images array
            const idx = images.findIndex(img => img === selectedVariant.image_url);
            if (idx !== -1) setCurrentImageIndex(idx);
        }
    }, [selectedVariant, modelSpecificImage, images]);

    const availableBranchesCount = product?.stock
        ? Object.values(product.stock).filter(Boolean).length
        : 0;

    const isOutOfStock = availableBranchesCount === 0;

    // Use variant availability if selected, else product general availability
    const isAvailable = selectedVariant
        ? currentModelStock
        : !isOutOfStock;

    // Determine displayed price (if variants had different prices, which they don't yet)


    const nextImage = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        if (images.length > 0) {
            setCurrentImageIndex((prev) => (prev + 1) % images.length);
        }
    };

    const prevImage = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        if (images.length > 0) {
            setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
        }
    };

    // Architect: Prevent background scroll only when zooming
    React.useEffect(() => {
        if (isZooming) {
            const originalStyle = window.getComputedStyle(document.body).overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = originalStyle;
            };
        }
    }, [isZooming]);

    // Fetch related products of the same brand
    React.useEffect(() => {
        if (product && product.make) {
            productService.getProductsByBrand(product.make).then(products => {
                const filtered = products.filter(p => p.id !== product.id);
                // Shuffle array
                const shuffled = filtered.sort(() => 0.5 - Math.random());
                setRelatedProducts(shuffled.slice(0, 4));
            });
        }
    }, [product]);



    return (
        <div className="w-full bg-black min-h-screen pt-20 pb-24 md:pb-0">
            {/* Top Navigation Bar */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 hidden md:flex items-center justify-between">
                <button
                    onClick={onBack}
                    className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors group"
                >
                    <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                    <span className="text-sm font-bold uppercase tracking-wider">Volver</span>
                </button>
            </div>

            <div className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8 pb-20">
                <div className="w-full flex flex-col lg:flex-row relative gap-0 lg:gap-12 items-start">
                    
                    {/* Image Section (Left Column) */}
                    <div className="w-full lg:w-1/2 relative flex flex-col min-h-[400px] md:min-h-[600px] shrink-0 border-b lg:border-none border-zinc-800">
                        <div className="absolute inset-0 bg-hex-pattern opacity-10" />
                        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 blur-[80px] rounded-full pointer-events-none" />

                        <div className="relative w-full h-full flex items-center justify-center group/image">
                                            <AnimatePresence mode="wait">
                                                <motion.img
                                                    key={`main-image-${currentImageIndex}`}
                                                    src={images[currentImageIndex]}
                                                    alt={`Case de fibra de carbono para ${product.name} - Vista ${currentImageIndex + 1}`}
                                                    initial={{ opacity: 0, x: 20 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    exit={{ opacity: 0, x: -20 }}
                                                    transition={{ duration: 0.3 }}
                                                    className="relative z-10 max-w-full max-h-full object-contain drop-shadow-[0_0_20px_rgba(255,255,255,0.1)] cursor-zoom-in"
                                                    onClick={() => setIsZooming(true)}
                                                />
                                            </AnimatePresence>

                                            {/* Zoom Hint */}
                                            <div className="absolute top-4 right-4 z-30 opacity-0 group-hover/image:opacity-100 transition-opacity pointer-events-none">
                                                <div className="bg-black/50 backdrop-blur text-white p-2 rounded-full">
                                                    <ZoomIn size={20} />
                                                </div>
                                            </div>

                                            {/* Navigation Arrows */}
                                            {images.length > 1 && (
                                                <div className="absolute inset-0 pointer-events-none">
                                                    <button
                                                        onClick={prevImage}
                                                        className="absolute left-2 top-1/2 -translate-y-1/2 z-30 p-2 bg-black/50 hover:bg-white text-white hover:text-black rounded-full backdrop-blur-sm transition-all opacity-0 group-hover/image:opacity-100 pointer-events-auto"
                                                    >
                                                        <ChevronLeft size={24} />
                                                    </button>
                                                    <button
                                                        onClick={nextImage}
                                                        className="absolute right-2 top-1/2 -translate-y-1/2 z-30 p-2 bg-black/50 hover:bg-white text-white hover:text-black rounded-full backdrop-blur-sm transition-all opacity-0 group-hover/image:opacity-100 pointer-events-auto"
                                                    >
                                                        <ChevronRight size={24} />
                                                    </button>

                                                    {/* Dots */}
                                                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex gap-2 p-2 bg-black/40 backdrop-blur-sm rounded-full pointer-events-auto">
                                                        {images.map((_, idx) => (
                                                            <button
                                                                key={`nav-dot-${idx}`}
                                                                onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(idx); }}
                                                                className={`w-2 h-2 rounded-full transition-all ${idx === currentImageIndex ? 'bg-white w-6' : 'bg-white/50 hover:bg-white'}`}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="absolute top-6 left-6 z-20 flex flex-col gap-2">
                                            {product.is_premium && (
                                                <div className="bg-white text-black text-xs font-black uppercase tracking-widest py-1.5 px-3 rounded shadow-lg flex items-center gap-1.5">
                                                    <Tag className="w-4 h-4" />
                                                    En oferta
                                                </div>
                                            )}

                                            {isOutOfStock && (
                                                <div className="bg-red-600 text-white text-xs font-black uppercase tracking-widest py-1.5 px-3 rounded shadow-lg flex items-center gap-1.5">
                                                    <AlertTriangle className="w-4 h-4 text-white" />
                                                    AGOTADO TEMPORALMENTE
                                                </div>
                                            )}
                                        </div>
                                     </div>

                                    {/* Info Section (Right Column - Sticky) */}
                                    <div className="w-full lg:w-1/2 flex flex-col h-auto pt-6 lg:pt-0 sticky top-24 pb-8">

                                        {/* Header */}
                                        <div className="px-6 lg:px-0 pb-4 shrink-0 transition-all">
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className="text-white text-[10px] md:text-xs font-bold uppercase tracking-wider border border-white/30 px-2 py-0.5 rounded">
                                                    {product.category}
                                                </span>
                                                <BrandLogo brandName={product.make || ''} className="w-4 h-4 md:w-5 md:h-5" noFilter />
                                                <span className="text-zinc-500 text-[10px] md:text-xs font-bold uppercase tracking-wider">
                                                    {product.make}
                                                </span>
                                            </div>
                                            <h2 className="text-lg md:text-3xl lg:text-4xl font-display font-black text-white italic uppercase tracking-tighter leading-[0.9]">
                                                {product.name}
                                            </h2>
                                        </div>

                                        {/* Scrollable Body - Eliminated internal scroll */}
                                        <div className="flex-1 px-6 lg:px-0 py-4 space-y-6">

                                            {/* Variant Selectors */}
                                            {variants.length > 0 && (
                                                <div className="space-y-4 md:space-y-6">
                                                    {/* Model Selector */}
                                                    {uniqueModels.length > 0 && (
                                                        <div className="space-y-3">
                                                            <label className="text-[10px] md:text-xs uppercase font-bold text-zinc-500 tracking-wider">Modelo / Variante</label>
                                                            <div className="flex flex-wrap gap-2 md:gap-3">
                                                                {uniqueModels.map(model => (
                                                                    <button
                                                                        key={model}
                                                                        onClick={() => {
                                                                            setSelectedModel(model);
                                                                            const isColorValidForNewModel = selectedColor && variants.some(v =>
                                                                                v.color_name === selectedColor &&
                                                                                (v.models || []).some(m => (m.model_name || 'Standard') === model)
                                                                            );
                                                                            if (!isColorValidForNewModel) {
                                                                                setSelectedColor(null);
                                                                            }
                                                                        }}
                                                                        className={`px-4 py-2 md:px-5 md:py-2.5 text-xs md:text-sm font-bold uppercase rounded-full border transition-all duration-300 shadow-sm ${selectedModel === model
                                                                            ? 'bg-white text-black border-white shadow-[0_0_15px_rgba(255,255,255,0.3)] scale-105'
                                                                            : 'bg-zinc-900/50 text-zinc-400 border-zinc-700 hover:border-zinc-500 hover:text-white hover:bg-zinc-800'}`}
                                                                    >
                                                                        {model}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Color / Texture Selector */}
                                                    {availableColors.length > 0 && (
                                                        <div className="space-y-3 pt-2">
                                                            <div className="flex items-center justify-between">
                                                                <label className="text-[10px] md:text-xs uppercase font-bold text-zinc-500 tracking-wider">
                                                                    Acabado: <span className="text-white ml-1">{selectedColor || 'SELECCIONA UN ACABADO'}</span>
                                                                </label>
                                                            </div>
                                                            <div className="flex flex-wrap gap-3 md:gap-4">
                                                                {availableColors.map(color => {
                                                                    const isSelected = selectedColor === color.name;
                                                                    const isTexture = color.texture && color.texture !== 'default';

                                                                    return (
                                                                        <button
                                                                            key={color.name}
                                                                            onClick={() => setSelectedColor(color.name)}
                                                                            title={color.name}
                                                                            className={`relative w-10 h-10 md:w-12 md:h-12 rounded-full border transition-all duration-300 overflow-hidden group/swatch shadow-lg ${isSelected
                                                                                ? 'ring-2 ring-white ring-offset-4 ring-offset-[#09090b] border-transparent scale-110 shadow-[0_0_20px_rgba(255,255,255,0.4)] z-10'
                                                                                : 'border-zinc-600 hover:border-white/50 opacity-80 hover:opacity-100 hover:scale-105'}`}
                                                                        >
                                                                            {/* Swatch Content */}
                                                                            <div className="w-full h-full relative">
                                                                                {/* Use real texture image if available, else use CSS simulation or solid color */}
                                                                                {color.texture_url ? (
                                                                                    <div 
                                                                                        className="absolute inset-0 bg-cover bg-center"
                                                                                        style={{ backgroundImage: `url(${color.texture_url})` }}
                                                                                    />
                                                                                ) : isTexture ? (
                                                                                    <div className="w-full h-full relative">
                                                                                        <div className={`absolute inset-0 ${
                                                                                            color.texture === 'carbon' ? 'bg-zinc-800' :
                                                                                            color.texture === 'forged' ? 'bg-zinc-700' :
                                                                                            color.texture === 'alcantara' ? 'bg-zinc-600' :
                                                                                            color.texture === 'leather' ? 'bg-[#3d2b1f]' :
                                                                                            'bg-zinc-800'
                                                                                        }`} />
                                                                                        
                                                                                        {color.texture === 'carbon' && (
                                                                                            <div className="absolute inset-0 opacity-40 bg-[repeating-linear-gradient(45deg,#000_0px,#000_2px,transparent_2px,transparent_4px)] mix-blend-overlay" />
                                                                                        )}
                                                                                        {color.texture === 'forged' && (
                                                                                            <div className="absolute inset-0 opacity-50 bg-[radial-gradient(ellipse_at_center,#fff_0%,#000_100%)] mix-blend-overlay" />
                                                                                        )}
                                                                                        {color.texture === 'alcantara' && (
                                                                                            <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_center,#fff_0%,transparent_100%),radial-gradient(circle_at_bottom_left,#000_0%,transparent_100%)] mix-blend-multiply" />
                                                                                        )}
                                                                                    </div>
                                                                                ) : (
                                                                                    <div
                                                                                        className="w-full h-full"
                                                                                        style={{ backgroundColor: color.hex || '#333' }}
                                                                                    />
                                                                                )}
                                                                                
                                                                                {/* Gloss/Shadow overlay for all swatches */}
                                                                                <div className="absolute inset-0 shadow-[inset_0_2px_4px_rgba(255,255,255,0.2),inset_0_-2px_6px_rgba(0,0,0,0.6)] rounded-full pointer-events-none" />
                                                                            </div>

                                                                            {!color.in_stock && (
                                                                                <div className="absolute inset-0 bg-black/60 flex items-center justify-center backdrop-blur-[1px]">
                                                                                    <div className="w-full h-[2px] bg-red-500 rotate-45 transform scale-150 shadow-[0_0_5px_rgba(239,68,68,0.8)]" />
                                                                                </div>
                                                                            )}
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Stock Status Banner */}
                                            <div className={`flex items-center gap-1.5 text-xs md:text-sm font-bold uppercase tracking-wide ${isAvailable ? 'text-white' : 'text-orange-500'}`}>
                                                {isAvailable ? <CheckCircle className="h-4 w-4 md:h-5 md:w-5" /> : <AlertTriangle className="h-4 w-4 md:h-5 md:w-5" />}
                                                {isAvailable ? 'En Stock' : 'Agotado'}
                                                {selectedVariant && !isAvailable && <span className="text-[10px] md:text-xs ml-1 text-zinc-500 normal-case">(variante no disponible)</span>}
                                            </div>

                                            {/* Specifications */}
                                            {product.description && (
                                                <div className="pt-2">
                                                    <h3 className="text-white font-display font-bold uppercase text-[10px] md:text-sm mb-2 md:mb-3 flex items-center gap-2">
                                                        <ChevronRight className="w-3 h-3 md:w-4 md:h-4 text-white" />
                                                        Especificaciones
                                                    </h3>
                                                    <div className="flex flex-col gap-4">
                                                        <p className="text-[11px] md:text-sm text-gray-400 leading-relaxed italic border-l-2 border-white/30 pl-3">
                                                            {product.description}
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Footer CTA */}
                                        <div className="shrink-0 px-6 lg:px-0 pt-8 border-t border-zinc-800/50 z-20 mt-8">
                                            <div className="flex flex-col sm:flex-row gap-3">
                                                <button
                                                    onClick={() => useCartStore.getState().addItem(product)}
                                                    className="w-full sm:w-auto bg-zinc-800 hover:bg-zinc-700 text-white px-4 md:px-8 py-4 flex items-center justify-center gap-2 font-display font-black uppercase transition-all border border-zinc-700 hover:border-zinc-500 shadow-lg"
                                                >
                                                    <ListPlus className="w-5 h-5" />
                                                    <span className="text-sm tracking-widest">Añadir al Carrito</span>
                                                </button>
                                                {isAvailable ? (
                                                    <button
                                                        onClick={() => setIsShippingModalOpen(true)}
                                                        className="flex-grow bg-white hover:bg-zinc-200 text-black font-display font-black uppercase text-sm tracking-widest py-4 px-4 flex items-center justify-center gap-2 transition-colors shadow-[0_0_20px_rgba(255,255,255,0.1)] hover:shadow-[0_0_30px_rgba(255,255,255,0.2)]"
                                                    >
                                                        <MessageCircle className="h-5 w-5 md:h-6 md:w-6" />
                                                        <span className="whitespace-normal text-center sm:text-left leading-tight flex flex-col justify-center">Solicitar Envío</span>
                                                    </button>
                                                ) : (
                                                    <WhatsAppProductButton
                                                        product={product}
                                                        currentBranch={currentBranch}
                                                        isAvailable={!!isAvailable}
                                                        buttonText="Consultar Reingreso"
                                                        selectedVariant={selectedVariant ? {
                                                            name: `${selectedModel} - ${selectedColor}`,
                                                            sku: selectedVariant.variant_sku || ''
                                                        } : undefined}
                                                        className="flex-grow bg-zinc-800 hover:bg-zinc-700 text-white font-display font-black uppercase text-sm tracking-widest py-4 flex items-center justify-center gap-2 transition-colors"
                                                        variant="large"
                                                    />
                                                )}
                                            </div>

                                            {/* Trust Badges */}
                                            <div className="grid grid-cols-3 gap-4 mt-8 pt-8 border-t border-zinc-800/30">
                                                <div className="flex flex-col items-center justify-center gap-2 text-center group">
                                                    <Truck className="w-5 h-5 text-zinc-400 group-hover:text-white transition-colors" strokeWidth={1.5} />
                                                    <span className="text-[10px] font-black uppercase tracking-wider text-zinc-300">Envío Nacional</span>
                                                </div>
                                                <div className="flex flex-col items-center justify-center gap-2 text-center group">
                                                    <ShieldCheck className="w-5 h-5 text-zinc-400 group-hover:text-white transition-colors" strokeWidth={1.5} />
                                                    <span className="text-[10px] font-black uppercase tracking-wider text-zinc-300">Garantía de Fábrica</span>
                                                </div>
                                                <div className="flex flex-col items-center justify-center gap-2 text-center group">
                                                    <Lock className="w-5 h-5 text-zinc-400 group-hover:text-white transition-colors" strokeWidth={1.5} />
                                                    <span className="text-[10px] font-black uppercase tracking-wider text-zinc-300">Pago Seguro</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                {/* Social Proof / Reviews Section */}
                {reviews.length > 0 && (
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-t border-zinc-800 mt-6">
                        <ReviewSummary reviews={reviews} />
                        <CustomerMediaCarousel reviews={reviews} />
                        <ReviewFeed reviews={reviews} />
                    </div>
                )}

                {/* Recomendados / También Compraron */}
                {relatedProducts.length > 0 && (
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-t border-zinc-800 mt-6">
                        <h2 className="text-2xl font-display font-black text-white italic uppercase tracking-widest mb-8 text-center">
                            También compraron
                        </h2>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                            {relatedProducts.map(relatedProduct => (
                                <ProductCard 
                                    key={relatedProduct.id}
                                    product={relatedProduct}
                                    currentBranch={currentBranch}
                                />
                            ))}
                        </div>
                    </div>
                )}

            {/* Zoom Modal - Outside main presence to avoid key collisions */}
            <AnimatePresence>
                {isZooming && (
                    <motion.div
                        key="zoom-modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
                        onClick={() => setIsZooming(false)}
                    >
                        <button
                            className="absolute top-4 right-4 text-white hover:text-white p-2 z-50"
                            onClick={() => setIsZooming(false)}
                        >
                            <X size={32} />
                        </button>

                        <button
                            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-white p-4 z-50 hidden sm:block"
                            onClick={prevImage}
                        >
                            <ChevronLeft size={48} />
                        </button>

                        <button
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-white p-4 z-50 hidden sm:block"
                            onClick={nextImage}
                        >
                            <ChevronRight size={48} />
                        </button>

                        <motion.img
                            key={`zoom-image-${currentImageIndex}`}
                            src={images[currentImageIndex]}
                            alt={`Detalle y textura de fibra de carbono para ${product.name} - Vista ${currentImageIndex + 1}`}
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="max-w-full max-h-full object-contain cursor-zoom-out"
                            onClick={(e) => e.stopPropagation()}
                        />

                        {/* Thumbnails in Zoom */}
                        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 overflow-x-auto max-w-full px-4 scrollbar-hide py-2">
                            {images.map((img, idx) => (
                                <button
                                    key={`thumb-${idx}`}
                                    onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(idx); }}
                                    className={`w-16 h-16 border-2 rounded-sm overflow-hidden flex-shrink-0 transition-all ${idx === currentImageIndex ? 'border-white opacity-100' : 'border-transparent opacity-50 hover:opacity-100'}`}
                                >
                                    <img src={img} alt={`Miniatura ${idx + 1} de ${product.name}`} className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Sticky Mobile CTA Bar */}
            <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-zinc-950/95 backdrop-blur-md border-t border-zinc-800 p-3 px-4 flex items-center justify-between gap-3 shadow-[0_-10px_30px_rgba(0,0,0,0.8)]">
                <div className="flex flex-col min-w-0 flex-1">
                    <span className="text-[11px] font-bold text-zinc-400 truncate">
                        {selectedModel || product.name}
                    </span>
                    <div className="flex items-center gap-1.5 mt-0.5">
                        <span className={`w-2 h-2 rounded-full ${isAvailable ? 'bg-emerald-500' : 'bg-orange-500'}`} />
                        <span className="text-xs font-black uppercase tracking-wider text-white">
                            {isAvailable ? 'Disponible' : 'Agotado'}
                        </span>
                        {product.price && (
                            <span className="text-xs font-mono font-bold text-zinc-300 ml-1">
                                ${product.price}
                            </span>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => useCartStore.getState().addItem(product)}
                        className="bg-zinc-900 border border-zinc-700 hover:border-zinc-500 text-white p-3 rounded-sm active:scale-95 transition-all"
                        aria-label="Añadir al carrito"
                        title="Añadir al carrito"
                    >
                        <ListPlus className="w-5 h-5" />
                    </button>
                    {isAvailable ? (
                        <button
                            onClick={() => setIsShippingModalOpen(true)}
                            className="bg-white hover:bg-zinc-200 text-black font-display font-black uppercase text-xs tracking-wider py-3 px-4 rounded-sm shadow-[0_0_15px_rgba(255,255,255,0.2)] active:scale-95 transition-all flex items-center gap-1.5"
                        >
                            <MessageCircle className="w-4 h-4" />
                            <span>Pedir Ahora</span>
                        </button>
                    ) : (
                        <WhatsAppProductButton
                            product={product}
                            currentBranch={currentBranch}
                            isAvailable={false}
                            buttonText="Consultar"
                            selectedVariant={selectedVariant ? {
                                name: `${selectedModel} - ${selectedColor}`,
                                sku: selectedVariant.variant_sku || ''
                            } : undefined}
                            className="bg-zinc-800 text-white font-display font-black uppercase text-xs tracking-wider py-3 px-3 rounded-sm"
                            variant="large"
                        />
                    )}
                </div>
            </div>

            <ShippingFormModal
                isOpen={isShippingModalOpen}
                onClose={() => setIsShippingModalOpen(false)}
                items={[{
                    product: product,
                    quantity: 1,
                    variantText: selectedVariant ? `${selectedModel} - ${selectedColor}` : undefined
                }]}
            />
        </div>
    );
};

export default ProductDetailView;
